/*
 * Lé Soleil – free Golden Pouch gift with 4+ glosses.
 * - When the cart reaches 4+ items, adds "The Golden Pouch – Free Gift" (price 0) once.
 * - If the items drop below 4, the free gift is removed (it is only free with 4+).
 * - If the customer removes the gift themselves, it is not added back until
 *   the cart drops below 4 and reaches 4 again.
 * - The regular Golden Pouch (199) and the free gift are not counted as items.
 * On/off switch: Online Store > Themes > Customize > section "Lé Soleil – Cart offers".
 */
(() => {
  'use strict';

  const SETTINGS = (window.LeSoleilOffers && window.LeSoleilOffers.pouch) || {};
  if (SETTINGS.enabled === false) return;

  const THRESHOLD = 4;
  const POUCH_VARIANT_ID = 49784091967703; // The Golden Pouch (199)
  const POUCH_HANDLE = 'the-golden-pouch';
  const GIFT_VARIANT_ID = 67752288354519; // The Golden Pouch – Free Gift (0)
  const GIFT_HANDLE = 'golden-pouch-gift-ls4g';
  const STATE_KEY = 'ls_pouch_gift_added';

  const root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
  const nativeFetch = window.fetch.bind(window);
  let timer = null;
  let busy = false;
  let again = false;

  const state = {
    get() { try { return sessionStorage.getItem(STATE_KEY) === '1'; } catch (_) { return false; } },
    set(on) { try { on ? sessionStorage.setItem(STATE_KEY, '1') : sessionStorage.removeItem(STATE_KEY); } catch (_) {} },
  };

  const isGift = (item) => item.handle === GIFT_HANDLE || Number(item.variant_id) === GIFT_VARIANT_ID;
  const isPouch = (item) =>
    isGift(item) || item.handle === POUCH_HANDLE || Number(item.variant_id) === POUCH_VARIANT_ID;

  function isCartMutation(url) {
    try {
      return /\/cart\/(add|change|update|clear)(\.js)?$/.test(new URL(String(url || ''), location.origin).pathname);
    } catch (_) { return false; }
  }

  async function getCart() {
    const res = await nativeFetch(root + '/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart.js ' + res.status);
    return res.json();
  }

  async function addGift() {
    const body = new URLSearchParams({ id: String(GIFT_VARIANT_ID), quantity: '1', 'properties[_free_pouch_gift]': 'true' });
    const res = await nativeFetch(root + '/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body: body.toString(),
    });
    if (!res.ok) throw new Error('add.js ' + res.status);
  }

  async function changeLine(key, quantity) {
    const res = await nativeFetch(root + '/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) throw new Error('change.js ' + res.status);
    return res.json();
  }

  // Refresh the cart drawer items using the theme's own "minicart-form" section (lightweight).
  async function refreshDrawer(itemCount) {
    document.querySelectorAll('.cart-count').forEach((el) => {
      el.textContent = el.classList.contains('cart-count-drawer') ? ` (${itemCount}) ` : itemCount;
    });
    const current = document.querySelector('#minicart_wrapper #minicart-form');
    if (!current) return;
    const res = await nativeFetch(root + '/?sections=minicart-form', { credentials: 'same-origin' });
    if (!res.ok) return;
    const data = await res.json();
    const html = data && data['minicart-form'];
    if (!html) return;
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('#minicart-form');
    if (next) current.innerHTML = next.innerHTML;
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'ls-free-pouch' } }));
  }

  async function afterChange(itemCount) {
    if (/\/cart\/?$/.test(location.pathname)) location.reload();
    else await refreshDrawer(itemCount);
  }

  async function sync() {
    if (busy) { again = true; return; }
    busy = true;
    try {
      const cart = await getCart();
      const count = cart.items.reduce((n, i) => n + (isPouch(i) ? 0 : i.quantity), 0);
      const gift = cart.items.find(isGift);
      const hasPouch = cart.items.some(isPouch);

      if (count < THRESHOLD) {
        state.set(false); // allow adding again next time
        if (gift) {
          // The gift is only free with 4+ items: take it out.
          const updated = await changeLine(gift.key, 0);
          await afterChange(updated.item_count);
        }
        return;
      }

      if (gift && gift.quantity > 1) {
        const updated = await changeLine(gift.key, 1);
        await afterChange(updated.item_count);
        return;
      }

      if (hasPouch || state.get()) return; // already there, or the customer removed it

      await addGift();
      state.set(true);
      await afterChange(cart.item_count + 1);
    } catch (err) {
      console.error('[Free pouch]', err);
    } finally {
      busy = false;
      if (again) { again = false; schedule(); }
    }
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sync, 350);
  }

  const prevFetch = window.fetch;
  window.fetch = function (...args) {
    const url = typeof args[0] === 'string' ? args[0] : args[0] && args[0].url;
    const p = prevFetch.apply(this, args);
    if (isCartMutation(url)) p.then((r) => { if (r.ok) schedule(); }).catch(() => {});
    return p;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__lsPouchCart = isCartMutation(url);
    return xhrOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function (...args) {
    if (this.__lsPouchCart) this.addEventListener('load', () => { if (this.status < 300) schedule(); });
    return xhrSend.apply(this, args);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
})();
